import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Plus, Trash2, ExternalLink, BookOpen, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../config/api';
import axios from 'axios';

const ReferencesSection = ({ section }) => {
  const { conlangId } = useParams();
  const [refs, setRefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRefs();
  }, [conlangId, section]);

  const fetchRefs = async () => {
    try {
      const res = await axios.get(`${API}/conlangs/${conlangId}/references/${section}`, { withCredentials: true });
      setRefs(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!url && !label) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/conlangs/${conlangId}/references/${section}`, {
        url: url || null, label: label || null
      }, { withCredentials: true });
      setRefs([...refs, res.data]);
      setUrl(''); setLabel(''); setAdding(false);
      toast.success('Reference added');
    } catch { toast.error('Failed to add reference'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (refId) => {
    try {
      await axios.delete(`${API}/conlangs/${conlangId}/references/${section}/${refId}`, { withCredentials: true });
      setRefs(refs.filter(r => r.ref_id !== refId));
      toast.success('Reference removed');
    } catch { toast.error('Failed to remove reference'); }
  };

  if (loading) return null;

  return (
    <div className="mt-8 pt-6 border-t border-border" data-testid={`references-${section}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-foreground-muted" />
          <h3 className="text-sm font-semibold text-foreground-muted uppercase tracking-wider">References</h3>
        </div>
        {!adding && (
          <Button variant="ghost" size="sm" onClick={() => setAdding(true)} data-testid={`add-ref-${section}-btn`}>
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        )}
      </div>

      {refs.length > 0 && (
        <div className="space-y-2 mb-3">
          {refs.map(r => (
            <div key={r.ref_id} className="flex items-center gap-2 group p-2 rounded-md hover:bg-accent/50 transition-colors">
              <div className="flex-1 min-w-0">
                {r.url ? (
                  <a href={r.url} target="_blank" rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm flex items-center gap-1"
                  >
                    {r.label || r.url}
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  </a>
                ) : (
                  <span className="text-foreground-muted text-sm">{r.label}</span>
                )}
                {r.url && r.label && (
                  <p className="text-foreground-subtle text-xs truncate">{r.url}</p>
                )}
              </div>
              <Button variant="ghost" size="sm"
                className="opacity-0 group-hover:opacity-100 text-destructive hover:text-destructive"
                onClick={() => handleDelete(r.ref_id)}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {adding && (
        <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="flex-1">
            <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="URL (optional)" className="bg-background border-border text-sm h-8" data-testid={`ref-url-${section}`} />
          </div>
          <div className="flex-1">
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="Description (optional)" className="bg-background border-border text-sm h-8" data-testid={`ref-label-${section}`} />
          </div>
          <div className="flex gap-1">
            <Button type="submit" size="sm" disabled={saving || (!url && !label)} className="h-8" data-testid={`ref-save-${section}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => { setAdding(false); setUrl(''); setLabel(''); }} className="h-8">
              Cancel
            </Button>
          </div>
        </form>
      )}

      {refs.length === 0 && !adding && (
        <p className="text-foreground-subtle text-xs italic">No references added yet.</p>
      )}
    </div>
  );
};

export default ReferencesSection;
